/* eslint-disable @typescript-eslint/no-misused-promises, @typescript-eslint/strict-boolean-expressions */

import {
  alpha,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Filter, FilterConfig } from 'types/tape';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import useIsMobile from 'hooks/useIsMobile';
import { useState } from 'react';
import FilterPanel from './FilterPanel';
import { HideSupportStyle } from 'components/shared/HideSupportStyle';

interface FiltersListProps {
  filters: FilterConfig[];
  selectedFilterId?: FilterConfig['id'];
  setSelectedFilter: (filter: FilterConfig | undefined) => void;
  onEditSave?: (filter: FilterConfig) => Promise<void>;
  onDelete?: (id: Filter['id']) => Promise<void>;
  editLoading?: boolean;
  deleteLoading?: boolean;
}

const FiltersList = ({
  filters,
  selectedFilterId,
  setSelectedFilter,
  onEditSave,
  onDelete,
  editLoading,
  deleteLoading,
}: FiltersListProps) => {
  return filters.length === 0 ? (
    <Typography sx={{ my: 2 }}>No filters here</Typography>
  ) : (
    <List>
      {filters.map((filter: FilterConfig) => {
        return (
          <FilterListItem
            key={filter.id}
            filter={filter}
            isSelected={selectedFilterId === filter.id}
            onSelect={setSelectedFilter}
            onEditSave={onEditSave}
            onDelete={onDelete}
            editLoading={editLoading}
            deleteLoading={deleteLoading}
          />
        );
      })}
    </List>
  );
};

interface FilterListItemProps {
  filter: FilterConfig;
  isSelected: boolean;
  onSelect: (filter: FilterConfig) => void;
  onEditSave?: (filter: FilterConfig) => Promise<void>;
  onDelete?: (id: FilterConfig['id']) => Promise<void>;
  editLoading?: boolean;
  deleteLoading?: boolean;
}

const FilterListItem = ({
  filter,
  isSelected,
  onSelect,
  onEditSave,
  onDelete,
  editLoading,
  deleteLoading,
}: FilterListItemProps) => {
  const isMobile = useIsMobile();
  const theme = useTheme();
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [name, setName] = useState<string>(filter.name ?? '');
  const [filters, setFilters] = useState<Filter[]>(filter.value ?? []);

  const onOpenEditor = () => setOpenEditDialog(true);
  const onCloseEditor = () => setOpenEditDialog(false);

  const onOpenDelete = () => setOpenDeleteDialog(true);
  const onCloseDelete = () => setOpenDeleteDialog(false);

  const onChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const onDeleteConfirm = async () => {
    await onDelete?.(filter.id);
  };

  const onEditConfirm = async () => {
    await onEditSave?.({
      ...filter,
      name,
      value: filters,
    });
    onCloseEditor();
  };

  return (
    <>
      <ListItemButton
        key={filter.id}
        sx={{
          borderRadius: 2,
          backgroundColor: isSelected
            ? alpha(theme.palette.sgGreen, 0.35)
            : 'inherit',
          transition: 'background-color 0.2s ease',
          ':hover': {
            backgroundColor: alpha(theme.palette.sgGreen, 0.1),
          },
        }}
        onClick={() => onSelect(filter)}
      >
        <ListItemText
          primary={filter.name}
          primaryTypographyProps={{ color: 'text.secondary', fontSize: 14 }}
        />
        <ListItemSecondaryAction>
          <Stack direction="row" gap={1} alignItems="center">
            {!filter.viewOnly ? (
              <>
                <IconButton
                  sx={{
                    color: 'text.secondary',
                  }}
                  data-ph-capture-attribute-element="tape-filter-edit"
                  onClick={(event) => {
                    event.stopPropagation(); // Prevent triggering ListItem onClick
                    onOpenEditor();
                  }}
                >
                  <EditRoundedIcon sx={{ width: 16, height: 16 }} />
                </IconButton>
                <IconButton
                  sx={{
                    color: 'text.secondary',
                  }}
                  data-ph-capture-attribute-element="tape-filter-delete"
                  onClick={(event) => {
                    event.stopPropagation(); // Prevent triggering ListItem onClick
                    onOpenDelete();
                  }}
                >
                  <DeleteRoundedIcon sx={{ width: 16, height: 16 }} />
                </IconButton>
              </>
            ) : (
              <IconButton
                sx={{ color: 'text.secondary' }}
                data-ph-capture-attribute-element="tape-filter-view"
                onClick={(event) => {
                  event.stopPropagation(); // Prevent triggering ListItem onClick
                  onOpenEditor();
                }}
              >
                <VisibilityRoundedIcon sx={{ width: 16, height: 16 }} />
              </IconButton>
            )}
          </Stack>
        </ListItemSecondaryAction>
      </ListItemButton>

      {/* Edit or view dialog */}
      <Dialog
        className="canvas-no-drag"
        maxWidth="md"
        open={openEditDialog}
        fullScreen={isMobile}
        onClose={onCloseEditor}
      >
        <DialogContent>
          <Stack sx={{ gap: 4, maxWidth: 450 }}>
            <Stack sx={{ gap: 1 }}>
              <Stack
                sx={{
                  flexDirection: 'row',
                  gap: 1,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  sx={{
                    color: filter.viewOnly
                      ? theme.palette.text.primary
                      : theme.palette.text.secondary,
                  }}
                >
                  {!filter.viewOnly ? 'FILTER NAME' : filter.name}
                </Typography>
                <IconButton
                  size="small"
                  color="primary"
                  data-ph-capture-attribute-element="tape-filter-editor-close"
                  onClick={onCloseEditor}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>

              {!filter.viewOnly && (
                <TextField
                  value={name}
                  onChange={onChangeName}
                  placeholder="My Custom Filter"
                  variant="outlined"
                  InputProps={{
                    sx: {
                      height: 28,
                      fontSize: 12,
                    },
                  }}
                  sx={{
                    width: '100%',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.sgGreen, 0.85),
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.sgGreen,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.sgGreen,
                    },
                  }}
                />
              )}
            </Stack>

            <FilterPanel
              filters={filters}
              onChangeFilters={(newFilters: Filter[]) => setFilters(newFilters)}
              viewOnly={filter.viewOnly}
              noSym={filter.noSym}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ paddingX: '24px' }}>
          {!filter.viewOnly ? (
            <>
              <Button
                size="small"
                variant="outlined"
                sx={{
                  textTransform: 'none',
                  width: '50%',
                  borderWidth: 2,
                  fontSize: 14,
                  ':hover': {
                    borderWidth: 2,
                  },
                }}
                onClick={onCloseEditor}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                sx={{
                  textTransform: 'none',
                  fontSize: 14,
                  width: '50%',
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.button.default,
                  boxShadow: 'none',
                  ':hover': {
                    backgroundColor: theme.palette.button.hover,
                  },
                }}
                onClick={onEditConfirm}
              >
                {editLoading ? (
                  <CircularProgress size="small" />
                ) : (
                  'Save Changes'
                )}
              </Button>
            </>
          ) : (
            <Button
              size="small"
              variant="outlined"
              sx={{
                textTransform: 'none',
                width: '100%',
                borderWidth: 2,
                fontSize: 14,
                ':hover': {
                  borderWidth: 2,
                },
              }}
              onClick={onCloseEditor}
            >
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete dialog */}
      <Dialog
        className="canvas-no-drag"
        sx={{ '& .MuiDialog-paper': { maxHeight: 435 } }}
        maxWidth="md"
        open={openDeleteDialog}
        fullScreen={isMobile}
        onClose={onCloseDelete}
      >
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the "{filter.name}" filter?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ paddingX: '24px' }}>
          <Button
            autoFocus
            size="small"
            sx={{
              textTransform: 'none',
              borderWidth: 2,
              fontSize: 14,
              minWidth: 100,
              ':hover': {
                borderWidth: 2,
              },
            }}
            onClick={onCloseDelete}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            sx={{
              textTransform: 'none',
              minWidth: 100,
              fontSize: 14,
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.button.default,
              boxShadow: 'none',
              ':hover': {
                backgroundColor: theme.palette.button.hover,
              },
            }}
            onClick={onDeleteConfirm}
          >
            {deleteLoading ? <CircularProgress size="small" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {isMobile && (openEditDialog || openDeleteDialog) && <HideSupportStyle />}
    </>
  );
};

export default FiltersList;
